// Feature: M004-F0718 (History and Billing tabs of the booking drawer). Tests: T-M004-0219.
//
// ONE history read per drawer, lazy (made the first time History or Billing is selected, never when the drawer opens
// on Booking), shared by both tabs. Core's sentences (headline, next_action, conditions[].message / next_action,
// can.*.reason, timeline summary) are shown VERBATIM (ACCT-TX-011): nothing here composes a sentence for them. The
// timeline itself is the shared TxTimeline (webui-history-drawer lane), not a copy.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { buildTxHistoryApi, classifyHistoryError, normaliseTxHistory, unwrap } from '../../services/txHistoryAdapter';
import TxTimeline, { LinkedDocuments, TxInvoiceAndLedger } from '../tx/TxTimeline';
import { amountText, conditionBadge, stageLabel } from '../tx/txStages';

const defaultApi = buildTxHistoryApi(api);

// The words for a failed read. WebUI-authored only where Core sent none (design 2.4a).
const ERROR_COPY = Object.freeze({
  not_found: 'This transaction is not available to you.',
  forbidden: 'You do not have permission to see this transaction.',
  bad_key: 'This transaction id is not valid.',
  step_up: 'Confirm your identity to see this transaction.',
  failed: 'Could not load the history of this transaction.',
});

// `enabled` flips to true the first time a tab that needs the read is selected; it never goes back, so the read is made
// once. `reload` forces a fresh read (the Try again button).
export function useBookingTxHistory({ orgId, handOffId, enabled, txApi = defaultApi }) {
  const [state, setState] = useState({ status: 'idle', model: null, errorKind: '' });
  const requested = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const load = useCallback(async () => {
    setState({ status: 'loading', model: null, errorKind: '' });
    try {
      const data = unwrap(await txApi.getHistory(orgId, handOffId));
      const model = normaliseTxHistory(data);
      if (!alive.current) return;
      setState(model ? { status: 'ready', model, errorKind: '' } : { status: 'error', model: null, errorKind: 'failed' });
    } catch (error) {
      if (!alive.current) return;
      setState({ status: 'error', model: null, errorKind: classifyHistoryError(error).kind });
    }
  }, [txApi, orgId, handOffId]);
  useEffect(() => {
    if (!enabled || requested.current || !orgId || !handOffId) return;
    requested.current = true;
    load();
  }, [enabled, orgId, handOffId, load]);
  return { ...state, reload: load };
}

const ReadState = ({ read, children }) => {
  if (read.status === 'idle' || read.status === 'loading') {
    return <p className="bkh-loading" role="status" data-testid="bkh-tx-loading">Loading the history of this transaction…</p>;
  }
  if (read.status === 'error') {
    return (
      <div role="alert" data-testid="bkh-tx-error" data-kind={read.errorKind}>
        <p>{ERROR_COPY[read.errorKind] || ERROR_COPY.failed}</p>
        {read.errorKind === 'failed' || read.errorKind === 'step_up' ? (
          <button type="button" className="btn btn-outline btn-sm" data-testid="bkh-tx-retry" onClick={read.reload}>Try again</button>
        ) : null}
      </div>
    );
  }
  return children(read.model);
};

export const BookingTxHistoryTab = ({ read }) => (
  <ReadState read={read}>
    {(model) => <TxTimeline history={model} onRetry={read.reload} />}
  </ReadState>
);

const CAN_ROWS = Object.freeze([
  ['move', 'Move between Advice and the invoice queue'],
  ['release', 'Release to the Ledger'],
  ['retry', 'Retry the hand-over'],
  ['edit_amount', 'Change the amount'],
]);

// The "where it is now" block (design 16.2): stage, Core's headline / next_action, conditions, can{} rows with Core's
// reasons, the amount when `view_amounts` allows it, invoice and ledger entry.
export const BookingTxBillingTab = ({ read }) => (
  <ReadState read={read}>
    {(model) => (
      <div className="bkh-tx-billing" data-testid="bkh-tx-billing">
        <p>
          <span className="status-badge" data-testid="bkh-tx-stage">{stageLabel(model.stage, model.stageDetail)}</span>
        </p>
        {model.headline ? <p data-testid="bkh-tx-headline">{model.headline}</p> : null}
        {model.nextAction ? <p data-testid="bkh-tx-next-action">{model.nextAction}</p> : null}
        {model.conditions.length ? (
          <ul className="bkh-tx-conditions" data-testid="bkh-tx-conditions">
            {model.conditions.map((condition) => (
              <li key={condition.code} data-code={condition.code}>
                <strong>{conditionBadge(condition.code)}</strong>
                {condition.message ? <span>{` ${condition.message}`}</span> : null}
                {condition.next_action ? <span>{` ${condition.next_action}`}</span> : null}
              </li>
            ))}
          </ul>
        ) : null}
        {model.canViewAmounts && model.pricing && model.pricing.amount_minor != null ? (
          <p data-testid="bkh-tx-amount">{amountText(model.pricing.amount_minor, model.pricing.currency)}</p>
        ) : null}
        {model.can ? (
          <ul className="bkh-tx-can" data-testid="bkh-tx-can">
            {CAN_ROWS.map(([key, label]) => {
              const action = model.can[key];
              if (!action) return null;
              return (
                <li key={key} data-can={key} data-allowed={action.allowed ? 'true' : 'false'}>
                  <span>{label}</span>
                  {action.allowed ? <span>{' '}Available</span> : <span data-testid={`bkh-tx-can-reason-${key}`}>{` ${action.reason || ''}`}</span>}
                </li>
              );
            })}
          </ul>
        ) : null}
        <TxInvoiceAndLedger history={model} />
        <LinkedDocuments documents={model.children} canAmounts={model.canViewAmounts} zone={model.timezone} />
      </div>
    )}
  </ReadState>
);
